/**
 * usePrefetchMarket — prefetch market containers into React Query cache.
 * Call on hover over the "Маркет" nav link so data is ready before the click.
 * Uses the SAME query keys as MarketPage ['market', 'containers'].
 */
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/client';

const STALE = 10 * 60 * 1000; // 10 min — matches MarketPage

async function fetchMarketContainers() {
  try {
    const { data } = await apiClient.get('/v3/containers/all', { timeout: 30_000 });
    const rawResult = (data as any)?.result ?? data ?? [];
    const all = Array.isArray(rawResult) ? rawResult : [];
    const childrenMap = new Map<string, any[]>();
    for (const c of all) {
      if (c.parentId) {
        const siblings = childrenMap.get(c.parentId) ?? [];
        siblings.push(c);
        childrenMap.set(c.parentId, siblings);
      }
    }
    const marketIds = new Set(all.filter((c: any) => c.access === 'market').map((c: any) => c.id));
    const courses = all.filter((c: any) => {
      if ((c as any).courseType === 'open') return true;
      if (c.access === 'archived') {
        if (!c.parentId) return true;
        return !all.some((p: any) => p.id === c.parentId && p.access === 'archived');
      }
      if (c.access === 'market') {
        if (!c.parentId) return true;
        return !marketIds.has(c.parentId);
      }
      return false;
    });
    return { courses, childrenMap };
  } catch (err) {
    console.error('fetchMarketContainers failed:', err);
    return { courses: [], childrenMap: new Map() };
  }
}

export function usePrefetchMarket() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.prefetchQuery({
      queryKey: ['market', 'containers'],
      queryFn: fetchMarketContainers,
      staleTime: STALE,
    });
  };
}
